import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarSheet } from '@/components/CalendarSheet';
import { DiaryActionChip } from '@/components/DiaryHeader/DiaryActionChip';
import { LessonTimesChip } from '@/components/DiaryHeader/LessonTimesChip';
import { CalendarSolid } from '@/components/Icons/CalendarSolid';
import { formatFullMonth, isSameDay, startOfToday } from '@/utils/date';

interface Props {
  isCurrentStudyYear: boolean;
  selectedDate: Date;
  minDate?: Date;
  maxDate?: Date;
  onDateChange: (date: Date) => void;
}

export const MobileDiaryHeader: React.FC<Props> = ({
  isCurrentStudyYear,
  selectedDate,
  minDate,
  maxDate,
  onDateChange,
}) => {
  const { t } = useTranslation();
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const today = startOfToday();
  const canJumpToToday = isCurrentStudyYear && !isSameDay(selectedDate, today);

  const selectDate = (date: Date) => {
    setIsCalendarOpen(false);
    onDateChange(date);
  };

  return (
    <div className="grid grid-cols-[1fr_minmax(0,auto)_1fr] items-center gap-3 px-4 pt-3">
      <LessonTimesChip className="justify-self-start" />

      <span className="truncate text-center text-subhead-14 text-foreground">
        {formatFullMonth(selectedDate)}
      </span>

      <div className="flex items-center gap-2 justify-self-end">
        {canJumpToToday && (
          <DiaryActionChip
            label={t('diary.today')}
            className="animate-fade-in"
            onClick={() => onDateChange(today)}
          />
        )}
        <button
          type="button"
          aria-label={t('global.selectDate')}
          className="flex size-5 cursor-pointer items-center justify-center text-primary"
          onClick={() => setIsCalendarOpen(true)}
        >
          <CalendarSolid className="size-5" />
        </button>
      </div>

      <CalendarSheet
        isOpen={isCalendarOpen}
        filledDate={selectedDate}
        minDate={minDate}
        maxDate={maxDate}
        onChange={selectDate}
        onClose={() => setIsCalendarOpen(false)}
      />
    </div>
  );
};
